import { EditableText } from '../ui';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  CalendarIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  PuzzlePieceIcon,
  TrashIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import React, { Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useBriefMutations } from '../../hooks/useBriefs';
import { SidebarTool, sidebarToolToPathName } from '../../utils/SidebarTool';
import { Brief } from '../../services/models/brief';
import { classNames } from '../../utils/class-names';
import DashboardNav from '../dashboard-nav/DashboardNav';

export interface BriefNavBarProps {
  brief: Brief;
  onDeleteBrief: () => void;
}

const briefNavigation = [
  { name: 'Edit', tool: SidebarTool.EDIT_BRIEF, icon: PencilIcon },
  { name: 'Schedule', tool: SidebarTool.EDIT_SCHEDULE, icon: CalendarIcon },
  { name: 'Recipients', tool: SidebarTool.EDIT_RECIPIENTS, icon: UsersIcon },
  { name: 'Integrations', tool: SidebarTool.EDIT_INTEGRATIONS, icon: PuzzlePieceIcon },
];

const BriefNavBar: React.FC<BriefNavBarProps> = ({ brief, onDeleteBrief }) => {
  const history = useHistory();
  const location = useLocation();
  const { updateBriefName, isUpdatingName } = useBriefMutations();

  const openSidebarTool = (tool: SidebarTool) => {
    const pathName = location.pathname;
    const toolPath = sidebarToolToPathName(tool);
    const paths = pathName.split('/').filter(x => x.length > 0);

    if (toolPath.length > 0) {
      paths.push(toolPath);
    }

    const newPathName = '/' + paths.join('/');

    history.push(newPathName);
  };

  const handleOnDeleteBrief = (brief: Brief) => {
    onDeleteBrief();
  };

  // TODO(Jose): better manage empty name
  const handleOnChangeBriefName = (value: string, briefName: string) => {
    if (value.length > 0 && briefName !== value) {
      updateBriefName(brief.id, value);
    } else {
      // empty state reverts to original value
    }
  };

  return (
    <DashboardNav>
      <div className="flex-1 flex">
        <h1 className="text-xl font-bold m-0 text-gray-900">
          <EditableText
            key={brief.id}
            defaultValue={brief.name}
            onConfirm={(value: string) => handleOnChangeBriefName(value, brief.name)}
            disabled={isUpdatingName}
          />
        </h1>
      </div>
      <div className="flex ml-4">
        <div className="flex flex-nowrap">
          {/* More Options */}
          <Menu as="div" className="hidden sm:block">
            <MenuButton className="bg-white rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary">
              <span className="sr-only">More Options</span>
              <EllipsisHorizontalIcon className="h-6 w-6" aria-hidden="true" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="z-10 origin-top-right absolute right-48 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black/5 divide-y divide-gray-100 focus:outline-hidden">
                <div className="py-1">
                  <MenuItem>
                    <button
                      className="text-gray-700 group flex w-full items-center px-4 py-2 text-sm"
                      onClick={() => handleOnDeleteBrief(brief)}
                    >
                      <TrashIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Delete Brief
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>

          {briefNavigation
            .slice(0)
            .reverse()
            .map(item => (
              <span key={item.name} className="hidden sm:block ml-3">
                <button
                  type="button"
                  className="bg-white rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                  onClick={() => openSidebarTool(item.tool)}
                >
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                  <span className="sr-only">{item.name}</span>
                </button>
              </span>
            ))}

          {/* Dropdown */}
          <Menu as="div" className="relative inline-block text-left sm:hidden">
            <MenuButton className="bg-white rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="z-10 origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black/5 divide-y divide-gray-100 focus:outline-hidden">
                <div className="py-1">
                  {briefNavigation.map(item => (
                    <MenuItem key={item.name}>
                      {({ focus }) => (
                        <button
                          className={classNames(
                            focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                            'group flex w-full items-center px-4 py-2 text-sm'
                          )}
                          onClick={() => openSidebarTool(item.tool)}
                        >
                          <item.icon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          {item.name}
                        </button>
                      )}
                    </MenuItem>
                  ))}
                </div>
                <div className="py-1">
                  <MenuItem>
                    <button
                      className="text-gray-700 group flex w-full items-center px-4 py-2 text-sm"
                      onClick={() => handleOnDeleteBrief(brief)}
                    >
                      <TrashIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Delete Brief
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>
    </DashboardNav>
  );
};

export default BriefNavBar;
